// Geometry for the read-only phase timeline strip (a non-linear-editor style
// overview that sits above the phase cards on a project).
//
// A project's phases are a contiguous date chain (see phaseDates.ts): each
// phase[i].endDate === phase[i+1].startDate. This module turns that chain into
// proportional segments laid out left→right, plus a "today" playhead position.
// It is pure (no React, no Date.now) so it can be unit-tested; callers pass the
// current day as a YYYY-MM-DD string.
//
// v1 is visual only. The layout uses flex-grow proportional to each phase's
// duration in days, so a contiguous chain renders flush with no gaps. Phases
// that don't yet have both dates are dropped from the strip and surfaced via
// `skippedCount` so the caller can hint that the schedule is incomplete.

import { diffDays, addDays } from './phaseDates';

export interface TimelineSegmentInput {
  id: string;
  phaseNumber: number;
  label: string;
  /** YYYY-MM-DD or null while still being built. */
  startDate: string | null;
  /** YYYY-MM-DD or null while still being built. */
  endDate: string | null;
  /** Booqable lifecycle status, drives the segment fill color. */
  status?: string | null;
  /** Promoted phases are committed Booqable orders (immutable in this flow). */
  promoted: boolean;
  /** Paid phases are billed against a finalized invoice — their window is frozen
   *  (rescheduling would diverge the money), so every touching boundary locks. */
  paid?: boolean;
  /** A pickup-only phase (the project-closing "Final pickup") carries no rental
   *  window — it hauls the last bin away on a single date. It's drawn as a
   *  terminal MARKER on the strip (at its `startDate`, the collection day), not a
   *  proportional segment, and exposes no draggable boundary of its own. */
  pickupOnly?: boolean;
}

export interface LaidOutSegment extends TimelineSegmentInput {
  /** Both dates are present here (undated inputs are filtered out). */
  startDate: string;
  endDate: string;
  /** Duration in whole days, floored at 1 so a same-day phase stays visible. */
  days: number;
  /** flex-grow weight for the strip — proportional to duration. */
  flexGrow: number;
}

export interface PlayheadPosition {
  /** The segment that contains today. */
  segmentId: string;
  /** 0–100: today's position within that segment, left-to-right. */
  leftPct: number;
}

/** A pickup-only phase drawn as a point on the strip rather than a segment. */
export interface TimelineMarker {
  id: string;
  label: string;
  /** The collection date (YYYY-MM-DD). */
  date: string;
  status?: string | null;
  /** 0–100: the marker's position across the project span, left-to-right. */
  leftPct: number;
}

export interface Timeline {
  /** Dated rental phases in phaseNumber order, laid out proportionally. */
  segments: LaidOutSegment[];
  /** Pickup-only phases (e.g. the final pickup) drawn as terminal markers. */
  markers: TimelineMarker[];
  /** Project span start (earliest delivery) or null when nothing is dated. */
  domainStart: string | null;
  /** Project span end (latest pickup) or null when nothing is dated. */
  domainEnd: string | null;
  /** Whole-day span across all dated phases (floored at 1). */
  totalDays: number;
  /** Today's playhead, or null when today falls outside the project span. */
  today: PlayheadPosition | null;
  /** Phases dropped from the strip because they lack one or both dates. */
  skippedCount: number;
}

/**
 * Lay out a phase chain into proportional timeline segments.
 *
 * @param input  phases in any order (sorted internally by phaseNumber)
 * @param todayYmd  current day as YYYY-MM-DD, for the playhead (optional)
 */
export function computeTimeline(
  input: TimelineSegmentInput[],
  todayYmd?: string | null,
): Timeline {
  const dated = input
    .filter((p): p is LaidOutSegment => !!p.startDate && !!p.endDate)
    .map((p) => p as TimelineSegmentInput & { startDate: string; endDate: string })
    .sort((a, b) => a.phaseNumber - b.phaseNumber);

  // Pickup-only phases (the final pickup) are points, not windows: split them out
  // so they render as terminal markers and never claim a proportional segment or
  // a draggable boundary. The rental phases alone anchor the project span.
  const rentals = dated.filter((p) => !p.pickupOnly);
  const markerPhases = dated.filter((p) => p.pickupOnly);

  // A marker needs a rental span to position against (its date is the swap hinge
  // off the last rental). With no rental dated, there's nothing to anchor to —
  // fold any pickup-only phases into the "not yet scheduled" count.
  if (rentals.length === 0) {
    return {
      segments: [],
      markers: [],
      domainStart: null,
      domainEnd: null,
      totalDays: 1,
      today: null,
      skippedCount: input.length - dated.length + markerPhases.length,
    };
  }

  const skippedCount = input.length - dated.length;

  const domainStart = rentals.reduce(
    (min, p) => (diffDays(p.startDate, min) > 0 ? p.startDate : min),
    rentals[0].startDate,
  );
  // The span ends at the latest rental pickup — or a marker's collection date if
  // one falls later — so a terminal marker always lands within [0, 100].
  const domainEnd = [...rentals.map((p) => p.endDate), ...markerPhases.map((p) => p.startDate)]
    .reduce((max, d) => (diffDays(max, d) > 0 ? d : max), rentals[0].endDate);
  const totalDays = Math.max(1, diffDays(domainStart, domainEnd));

  const segments: LaidOutSegment[] = rentals.map((p) => {
    const days = Math.max(1, diffDays(p.startDate, p.endDate));
    return { ...p, days, flexGrow: days };
  });

  const markers: TimelineMarker[] = markerPhases.map((p) => {
    const into = diffDays(domainStart, p.startDate);
    const leftPct = Math.min(100, Math.max(0, (into / totalDays) * 100));
    return { id: p.id, label: p.label, date: p.startDate, status: p.status, leftPct };
  });

  let today: PlayheadPosition | null = null;
  if (todayYmd) {
    const afterStart = diffDays(domainStart, todayYmd) >= 0;
    const beforeEnd = diffDays(todayYmd, domainEnd) >= 0;
    if (afterStart && beforeEnd) {
      // Place the playhead inside whichever segment's [start, end] window holds
      // today. Walking in order, the first segment whose end is >= today wins
      // (shared hinges mean today lands on the earlier phase at a boundary).
      const hit = segments.find((s) => diffDays(todayYmd, s.endDate) >= 0);
      if (hit) {
        const into = diffDays(hit.startDate, todayYmd);
        const leftPct = Math.min(100, Math.max(0, (into / hit.days) * 100));
        today = { segmentId: hit.id, leftPct };
      }
    }
  }

  return { segments, markers, domainStart, domainEnd, totalDays, today, skippedCount };
}

// --- Interactive boundaries ---------------------------------------------------
// The strip's draggable affordances are its phase BOUNDARIES (the swap hinges
// plus the two outer edges). A laid-out chain of N segments exposes N+1
// boundaries B0..BN (see phaseDates.ts for the same model on the data side):
//
//   B0  = first delivery   → edits segment[0]'s start (ripple slides the chain)
//   Bk  = an interior hinge → edits segment[k-1]'s end (== segment[k]'s start)
//   BN  = last pickup       → edits segment[N-1]'s end
//
// Each boundary maps to a single (phaseId, edge) edit so a drag/keypress can be
// fed straight into applyPhaseEdit. A promoted phase is a live Booqable order,
// but staff reschedule it from this timeline (the child order surface keeps its
// dates read-only) — so a promoted phase's OWN edges move and the change is
// pushed to Booqable on save. A boundary locks when (a) it touches a PAID phase
// — that window is frozen against a finalized invoice — or (b) it's an interior
// hinge shared by TWO promoted phases, which can't shift without rescheduling
// both reservations at once.

export interface TimelineBoundary {
  /** Position in the boundary array, 0..segments.length. */
  index: number;
  /** Current date this boundary sits on (YYYY-MM-DD). */
  date: string;
  /** The phase whose edge this boundary drives. */
  phaseId: string;
  /** Which edge of that phase moves when this boundary is dragged. */
  edge: 'start' | 'end';
  /** Locked only when this hinge is shared by two promoted phases — moving it
   *  would reschedule both live reservations at once (the cascade won't). A
   *  boundary touching a single promoted phase stays editable: the change is
   *  pushed to that one Booqable order on save. */
  locked: boolean;
}

/** Derive the editable boundaries (B0..BN) for a laid-out timeline. */
export function timelineBoundaries(segments: LaidOutSegment[]): TimelineBoundary[] {
  const n = segments.length;
  if (n === 0) return [];

  const out: TimelineBoundary[] = [
    {
      index: 0,
      // The first delivery touches only phase 0 — editable unless that phase is
      // paid (frozen window); moving it reschedules at most that one phase.
      date: segments[0].startDate,
      phaseId: segments[0].id,
      edge: 'start',
      locked: !!segments[0].paid,
    },
  ];
  // Interior hinge Bk (1..n-1): drives the LEFT phase's end. Locked when either
  // side is PAID (frozen window), or when BOTH sides are promoted — that hinge
  // can't move without rescheduling two live reservations. One promoted side is
  // fine (it gets pushed to Booqable; the draft side absorbs).
  for (let k = 1; k < n; k++) {
    out.push({
      index: k,
      date: segments[k - 1].endDate,
      phaseId: segments[k - 1].id,
      edge: 'end',
      locked:
        !!segments[k - 1].paid ||
        !!segments[k].paid ||
        (segments[k - 1].promoted && segments[k].promoted),
    });
  }
  // BN — last pickup. Touches only the last phase — editable unless it's paid.
  out.push({
    index: n,
    date: segments[n - 1].endDate,
    phaseId: segments[n - 1].id,
    edge: 'end',
    locked: !!segments[n - 1].paid,
  });
  return out;
}

/**
 * Clamp a moved boundary so the phases on either side stay at least one day.
 *
 * Lower bound (the left neighbor) always applies: that phase's left edge never
 * moves with this edit, so the boundary can't cross it. The first boundary (B0)
 * has no left neighbor and is left unclamped on that side.
 *
 * Upper bound (`maxBoundaryDate`) is only supplied when the edit will be
 * ABSORBED against a promoted phase to the right — ripple normally slides the
 * downstream boundary along, but it stops dead at a promoted wall, so the
 * boundary must not cross that pinned edge and invert the promoted window.
 */
export function clampBoundaryDate(
  prevBoundaryDate: string | null,
  candidate: string,
  maxBoundaryDate: string | null = null,
): string {
  let result = candidate;
  if (prevBoundaryDate) {
    const minDate = addDays(prevBoundaryDate, 1);
    if (diffDays(minDate, result) < 0) result = minDate;
  }
  if (maxBoundaryDate) {
    const maxDate = addDays(maxBoundaryDate, -1);
    if (diffDays(result, maxDate) < 0) result = maxDate;
  }
  return result;
}
